import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useConfig } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { Spinner } from './ui';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute wraps routes that require authentication and configuration.
 *
 * Authentication flow:
 * 1. If auth is loading, show loading spinner
 * 2. If not authenticated, redirect to /login
 * 3. If authenticated but not configured, redirect to /setup
 * 4. If authenticated and configured, render children
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const location = useLocation();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { data: config, isLoading: configLoading, isError: configError } = useConfig();

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Spinner size="lg" className="text-blue-600 mb-4" />
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Show loading state while checking configuration
  if (configLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Spinner size="lg" className="text-blue-600 mb-4" />
          <p className="text-gray-600">Loading configuration...</p>
        </div>
      </div>
    );
  }

  // If there's an error fetching config, assume not configured
  // This handles cases where the API isn't available yet
  if (configError) {
    return <Navigate to="/setup" state={{ from: location }} replace />;
  }

  // If not configured, redirect to setup
  if (config && !config.isConfigured) {
    return <Navigate to="/setup" state={{ from: location }} replace />;
  }

  // Authenticated and configured - render the protected content
  return <>{children}</>;
};

ProtectedRoute.displayName = 'ProtectedRoute';

export default ProtectedRoute;
